import React from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { useContracts } from '../../../core/hooks/useContracts';
import { useAuth } from '../../../core/contexts/AuthContext';
import type { Job, JobStatus, JobPriority } from '../../../core/models/Job';
import { format } from 'date-fns';

interface JobDialogProps {
  open: boolean;
  onClose: () => void;
  job?: Partial<Job>;
  onSubmit: (data: any) => Promise<void>;
}

export const JobDialog: React.FC<JobDialogProps> = ({
  open,
  onClose,
  job,
  onSubmit,
}) => {
  const { organizationId } = useAuth();
  const { contracts } = useContracts(organizationId);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      contractId: job?.contractId || '',
      title: job?.title || '',
      description: job?.description || '',
      status: job?.status || 'draft' as JobStatus,
      priority: job?.priority || 'medium' as JobPriority,
      dueDate: job?.dueDate ? format(new Date(job.dueDate), 'yyyy-MM-dd') : '',
    },
    mode: 'onChange',
  });

  React.useEffect(() => {
    if (!job) {
      register('contractId', { required: 'Contract is required' });
    }
  }, [register, job]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{job ? 'Edit Job' : 'Create Job'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Contract <span className="text-error">*</span>
            </label>
            <Select
              value={watch('contractId')}
              onValueChange={(value) => setValue('contractId', value, { shouldValidate: true })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select contract" />
              </SelectTrigger>
              <SelectContent>
                {contracts.map((contract) => (
                  <SelectItem key={contract.id} value={contract.id}>
                    {contract.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.contractId && (
              <p className="text-sm text-error">{errors.contractId.message as string}</p>
            )}
          </div>
          <Input
            label="Job Title"
            {...register('title', { required: 'Title is required' })}
            error={errors.title?.message}
          />
          <Textarea
            label="Description"
            {...register('description')}
            rows={4}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={watch('status')}
                onValueChange={(value) => setValue('status', value as JobStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Priority</label>
              <Select
                value={watch('priority')}
                onValueChange={(value) => setValue('priority', value as JobPriority)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Input
            label="Due Date"
            type="date"
            {...register('dueDate')}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {job ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
